/**
 * 단어 시험 화면(DEV-I-020). 한 단(최대 720px, list-main), 모바일 우선. 상태는 App이 갖고 이 함수는 상태로 화면 하나를 만든다.
 *  - 목록(내 시험) → 시험 시작 → 문항 하나씩(4지선다, 반응시간 자동 기록) → 제출 → 다음 문항 → 전체 결과 → 새 시험
 *  - 모든 문항을 마치기 전에는 정답·뜻·정오를 어떤 형태로도 보이지 않는다(서버도 보내지 않음)
 *  - 결과가 중단(stopped)이거나 result null이면 점수·정오·정답 뜻을 보이지 않고 낸 응답만 보인다
 *  - 임시 데이터(sourceKind demo)는 '시험용 예시'로 표시한다. 실제 학습 콘텐츠의 시험 문항 승인이 아니다
 *  - 단어 시험 결과는 과제(GR)·근거 문제 결과와 합산하지 않는다
 */
import { statusLabel, type PendingAnswer, type Quiz, type QuizListRow, type QuizResult, nextUnanswered } from '../core/quiz-model.js';
import { h } from './dom.js';

export type QuizPhase = 'loading' | 'ok' | 'error' | 'denied' | 'notfound';
export type StartNote = null | 'no_valid_words' | 'no_prepared_questions' | 'start_uncertain' | 'start_error' | 'start_conflict' | 'start_denied';
export type SubmitNote = null | 'uncertain' | 'key_conflict' | 'already_answered' | 'rejected' | 'stopped' | 'notfound';

export interface QuizState {
  readonly view: 'list' | 'play' | 'result';
  phase: QuizPhase;
  errorCause: string | null;
  busy: boolean;
  seq: number;
  checkedAt: number | null;
  everLoaded: boolean;
  quizId: string | null;
  rows: QuizListRow[] | null;
  quiz: Quiz | null;
  result: QuizResult | null;
  /** 결과 조회 409 quiz_not_finished */
  notFinished: boolean;
  /** 문항 응답 입력(제출 전 화면 값) */
  draftItemId: string | null;
  draftOption: string | null;
  submitting: boolean;
  submitNote: SubmitNote;
  /** 시작 요청 중/미확정 */
  starting: boolean;
  startNote: StartNote;
  /** 새로 그려도 초점을 되돌릴 대상 */
  focusAfter: string | null;
}

export const blankQuiz = (view: QuizState['view'], quizId: string | null): QuizState => ({
  view, phase: 'loading', errorCause: null, busy: false, seq: 0, checkedAt: null, everLoaded: false, quizId, rows: null, quiz: null, result: null, notFinished: false,
  draftItemId: null, draftOption: null, submitting: false, submitNote: null, starting: false, startNote: null, focusAfter: null,
});

export interface QuizHandlers {
  readonly refresh: () => void;
  readonly toWords: () => void;
  readonly toList: () => void;
  readonly start: () => void;
  readonly open: (row: QuizListRow) => void;
  readonly pick: (optionId: string) => void;
  readonly submit: () => void;
  readonly resend: () => void;
  readonly toResult: () => void;
  readonly toPlay: () => void;
  /** 미확정 제출 기록(같은 키·같은 내용). 이 시험의 것만 */
  readonly pending: () => PendingAnswer | null;
  readonly startPending: () => boolean;
}

const hhmm = (t: number) => { const d = new Date(t); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const kst = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
};

function demoBadge(kind: string): HTMLElement | null {
  return kind === 'demo' ? h('p', { class: 'qdemo', 'data-testid': 'quiz-demo' }, '시험용 예시예요. 실제 학습 문제가 아니에요.') : null;
}

function errorText(S: QuizState): [string, string] {
  const title = S.view === 'list' ? '내 시험을 불러오지 못했어요.' : S.view === 'result' ? '결과를 불러오지 못했어요.' : '시험을 불러오지 못했어요.';
  const body = S.errorCause === 'timeout' ? '정해진 시간 안에 받아 오지 못했어요. 다시 확인해 주세요.'
    : S.errorCause === 'network' ? '연결을 확인한 뒤 다시 확인해 주세요.' : '다시 확인해 주세요.';
  return [title, body];
}

function stateBox(S: QuizState, H: QuizHandlers): HTMLElement | null {
  if (S.phase === 'ok') return null;
  if (S.phase === 'loading') return h('p', { class: 'loading', role: 'status', 'data-testid': 'quiz-loading' }, '불러오고 있어요');
  if (S.phase === 'notfound') {
    return h('section', { class: 'rstat', 'data-k': 'pending', 'data-testid': 'quiz-state', 'data-state': 'notfound', role: 'status' },
      h('h2', { 'data-testid': 'quiz-state-title', tabindex: '-1' }, '이 시험을 지금은 볼 수 없어요.'));
  }
  if (S.phase === 'denied') {
    return h('section', { class: 'rstat', 'data-k': 'error', 'data-testid': 'quiz-state', 'data-state': 'denied', role: 'alert' },
      h('h2', { 'data-testid': 'quiz-state-title', tabindex: '-1' }, '지금은 단어 시험을 볼 수 없어요.'));
  }
  const [t, b] = errorText(S);
  return h('section', { class: 'rstat', 'data-k': 'error', 'data-testid': 'quiz-state', 'data-state': 'error', 'data-cause': S.errorCause ?? '', role: 'alert' },
    h('h2', { 'data-testid': 'quiz-state-title', tabindex: '-1' }, t), h('p', { 'data-testid': 'quiz-state-body' }, b),
    h('div', { class: 'rcheck' }, h('button', { class: 'secondary', 'data-testid': 'quiz-retry', disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '다시 확인')));
}

const START_NOTES: Record<Exclude<StartNote, null>, [string, string]> = {
  no_valid_words: ['지금 시험 볼 단어가 없어요.', '단어장에 시험 볼 수 있는 단어가 생기면 다시 시작할 수 있어요.'],
  no_prepared_questions: ['준비된 시험 문제가 아직 없어요.', '단어는 있지만 검토를 마친 시험 문제가 아직 없어요. 나중에 다시 확인해 주세요.'],
  start_uncertain: ['시험이 시작됐는지 확인하지 못했어요.', '같은 요청으로 다시 확인할 수 있어요. 연결을 확인한 뒤 눌러 주세요.'],
  start_error: ['시험을 시작하지 못했어요.', '잠시 뒤 다시 시도해 주세요.'],
  start_conflict: ['시작 요청을 확인하지 못했어요.', '시험 목록을 다시 확인한 뒤 새로 시작해 주세요.'],
  start_denied: ['지금은 단어 시험을 볼 수 없어요.', ''],
};

function startBox(S: QuizState, H: QuizHandlers): HTMLElement | null {
  if (S.startNote === null) return null;
  const [t, b] = START_NOTES[S.startNote];
  const again = S.startNote === 'start_uncertain';
  return h('section', { class: 'rstat qstart-note', 'data-k': again || S.startNote === 'start_error' || S.startNote === 'start_denied' || S.startNote === 'start_conflict' ? 'error' : 'pending', 'data-testid': 'quiz-start-note', 'data-note': S.startNote, role: 'status' },
    h('h3', { tabindex: '-1', 'data-testid': 'quiz-start-note-title' }, t), b === '' ? null : h('p', {}, b),
    again ? h('button', { class: 'secondary', 'data-testid': 'quiz-start-resend', disabled: S.starting, onclick: H.start }, S.starting ? '확인하는 중' : '같은 요청으로 다시 확인') : null);
}

export function renderQuizList(S: QuizState, H: QuizHandlers): HTMLElement {
  const root = h('div', { class: 'list-main quiz', 'data-testid': 'quiz', 'data-view': 'list', 'data-phase': S.phase });
  root.append(h('div', { class: 'lhead' }, h('h2', { 'data-testid': 'quiz-title', tabindex: '-1' }, '단어 시험')));
  root.append(h('p', { class: 'qlead' }, '내 단어장의 단어로 한 번에 최대 5문제를 풀어요.'));
  const box = stateBox(S, H);
  if (box !== null) root.append(box);
  if (S.phase !== 'ok') { const sb = startBox(S, H); if (sb !== null) root.append(sb); }
  if (S.phase === 'ok' && S.rows !== null) {
    const pending = H.startPending();
    root.append(h('div', { class: 'rcheck wcheck' },
      h('span', { 'data-testid': 'quiz-checked-at' }, S.checkedAt !== null ? `확인한 시각 ${hhmm(S.checkedAt)} (이 기기 시계)` : ''),
      h('button', { class: 'secondary', 'data-testid': 'quiz-refresh', disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '다시 확인')));
    root.append(h('button', { class: 'primary qstart', 'data-testid': 'quiz-start', disabled: S.starting || S.busy || pending, onclick: H.start }, S.starting ? '시작하는 중' : '새 시험 시작'));
    const sb = startBox(S, H);
    if (sb !== null) root.append(sb);
    if (S.rows.length === 0) root.append(h('p', { class: 'wempty', 'data-testid': 'quiz-empty', tabindex: '-1' }, '아직 본 시험이 없어요.'));
    else {
      root.append(h('ul', { class: 'wlist', 'data-testid': 'quiz-list', 'aria-busy': S.busy ? 'true' : 'false' }, ...S.rows.map((r) => h('li', {}, h('button', {
        class: 'wrow', 'data-testid': `qrow-${r.quizId}`, 'data-quiz': r.quizId, 'data-status': r.status, disabled: S.busy, onclick: () => { if (!S.busy) H.open(r); },
      },
      h('span', { class: 'whw', 'data-testid': 'qrow-status' }, statusLabel(r.status)),
      h('span', { class: 'wmn' }, `${kst(r.startedAt)} 시작 · ${r.progress.answered}/${r.progress.total} 문항 응답`),
      r.sourceKind === 'demo' ? h('span', { class: 'wmeta', 'data-testid': 'qrow-demo' }, '시험용 예시') : null)))));
    }
  }
  root.append(h('div', { class: 'wfoot' }, h('button', { class: 'secondary', 'data-testid': 'quiz-to-words', onclick: H.toWords }, '내 단어장으로')));
  return root;
}

function optionLabel(q: Quiz, itemId: string, optionId: string | null): string | null {
  const it = q.items.find((i) => i.itemId === itemId);
  return it?.options.find((o) => o.optionId === optionId)?.label ?? null;
}

export function renderQuizPlay(S: QuizState, H: QuizHandlers): HTMLElement {
  const root = h('div', { class: 'list-main quiz', 'data-testid': 'quiz', 'data-view': 'play', 'data-phase': S.phase });
  const box = stateBox(S, H);
  if (box !== null) { root.append(box, h('div', { class: 'wfoot' }, h('button', { class: 'secondary', 'data-testid': 'quiz-to-list', onclick: H.toList }, '시험 목록으로'))); return root; }
  const q = S.quiz;
  if (q === null) return root;
  root.append(h('div', { class: 'lhead' }, h('h2', { 'data-testid': 'quiz-title', tabindex: '-1' }, '단어 시험')));
  const demo = demoBadge(q.sourceKind);
  if (demo !== null) root.append(demo);

  if (q.status === 'stopped' || S.submitNote === 'stopped') {
    root.append(h('section', { class: 'rstat', 'data-k': 'pending', 'data-testid': 'quiz-stopped', role: 'status' },
      h('h3', { 'data-testid': 'quiz-stopped-title', tabindex: '-1' }, '시험이 중단됐어요.'),
      h('p', {}, '시험 문제의 근거가 바뀌어 이어서 풀 수 없어요. 이미 낸 응답은 그대로 남아 있어요. 새 시험으로 다시 풀어 주세요.')));
    root.append(h('div', { class: 'wfoot' }, h('button', { class: 'primary', 'data-testid': 'quiz-new', onclick: H.start }, '새 시험 시작'), h('button', { class: 'secondary', 'data-testid': 'quiz-to-list', onclick: H.toList }, '시험 목록으로')));
    return root;
  }
  if (q.status === 'completed') {
    root.append(h('section', { class: 'rstat', 'data-k': 'ok', 'data-testid': 'quiz-all-done', role: 'status' },
      h('h3', { 'data-testid': 'quiz-all-done-title', tabindex: '-1' }, '모든 문제에 답했어요.'), h('p', {}, '결과에서 맞음·틀림과 정답 뜻을 볼 수 있어요.')));
    root.append(h('div', { class: 'wfoot' }, h('button', { class: 'primary', 'data-testid': 'quiz-to-result', onclick: H.toResult }, '결과 보기'), h('button', { class: 'secondary', 'data-testid': 'quiz-to-list', onclick: H.toList }, '시험 목록으로')));
    return root;
  }

  const item = nextUnanswered(q);
  if (item === null) return root;
  const pend = H.pending();
  const locked = pend !== null && pend.quizId === q.quizId && pend.itemId === item.itemId;
  const chosen = locked ? pend.optionId : S.draftItemId === item.itemId ? S.draftOption : null;
  const answered = q.progress.answered;
  root.append(h('p', { class: 'qprog', role: 'status', 'data-testid': 'quiz-progress' }, `${Math.min(answered + 1, q.progress.total)} / ${q.progress.total} 문항`));
  root.append(h('section', { class: 'qcard', 'data-testid': 'quiz-item', 'data-item': item.itemId, 'data-order': String(item.order) },
    h('h3', { class: 'qhw', lang: 'en', 'data-testid': 'quiz-headword', tabindex: '-1' }, item.headword),
    h('p', { class: 'qprompt', 'data-testid': 'quiz-prompt' }, item.prompt),
    h('fieldset', { class: 'qopts', 'data-testid': 'quiz-options', disabled: S.submitting || locked }, h('legend', {}, '알맞은 뜻을 하나 고르세요'),
      ...item.options.map((o) => h('label', { class: 'qopt', 'data-checked': chosen === o.optionId ? 'true' : 'false' },
        h('input', { type: 'radio', name: `opt-${item.itemId}`, value: o.optionId, checked: chosen === o.optionId, 'data-testid': `qopt-${o.order}`, onchange: () => H.pick(o.optionId) }),
        h('span', {}, o.label))))));

  if (S.submitNote === 'already_answered') root.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'quiz-note', 'data-note': 'already_answered' }, '이 문제는 이미 답을 냈어요. 낸 답은 바꿀 수 없어요.'));
  if (S.submitNote === 'key_conflict') root.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'quiz-note', 'data-note': 'key_conflict' }, '이 제출 요청을 확인하지 못했어요. 낸 답이 있는지 다시 확인한 뒤, 필요하면 답을 다시 골라 제출해 주세요.'));
  if (S.submitNote === 'rejected') root.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'quiz-note', 'data-note': 'rejected' }, '답을 받지 못했어요. 보기를 다시 확인한 뒤 제출해 주세요.'));
  if (S.submitNote === 'notfound') root.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'quiz-note', 'data-note': 'notfound' }, '이 시험을 지금은 볼 수 없어요.'));
  if (locked && pend !== null) {
    root.append(h('section', { class: 'rstat', 'data-k': 'error', 'data-testid': 'quiz-uncertain', role: 'alert' },
      h('h3', { 'data-testid': 'quiz-uncertain-title', tabindex: '-1' }, S.submitting ? '답을 보내고 있어요.' : '답이 제출됐는지 확인하지 못했어요.'),
      h('p', {}, '확인될 때까지 고른 답은 바꿀 수 없어요. 같은 답으로만 다시 확인해요.'),
      h('button', { class: 'primary', 'data-testid': 'quiz-resend', disabled: S.submitting, onclick: H.resend }, S.submitting ? '확인하는 중' : '같은 답으로 다시 확인')));
  } else {
    root.append(h('button', {
      class: 'primary qsubmit', 'data-testid': 'quiz-submit', disabled: chosen === null || S.submitting,
      // 보기를 고르지 않으면 제출하지 않는다(단추 비활성 + 눌려도 막음)
      onclick: () => { if (chosen !== null && !S.submitting) H.submit(); },
    }, S.submitting ? '제출하는 중' : '답 제출'));
    if (chosen === null) root.append(h('p', { class: 'qhint', 'data-testid': 'quiz-hint' }, '보기를 하나 골라야 제출할 수 있어요.'));
  }
  root.append(h('div', { class: 'wfoot' }, h('button', { class: 'secondary', 'data-testid': 'quiz-to-list', onclick: H.toList }, '시험 목록으로')));
  return root;
}

export function renderQuizResult(S: QuizState, H: QuizHandlers): HTMLElement {
  const root = h('div', { class: 'list-main quiz', 'data-testid': 'quiz', 'data-view': 'result', 'data-phase': S.phase });
  root.append(h('div', { class: 'lhead' }, h('h2', { 'data-testid': 'quiz-title', tabindex: '-1' }, '단어 시험 결과')));
  const box = stateBox(S, H);
  const foot = h('div', { class: 'wfoot' }, h('button', { class: 'secondary', 'data-testid': 'quiz-to-list', onclick: H.toList }, '시험 목록으로'));
  if (box !== null) { root.append(box, foot); return root; }
  if (S.notFinished) {
    root.append(h('section', { class: 'rstat', 'data-k': 'pending', 'data-testid': 'quiz-not-finished', role: 'status' },
      h('h3', { 'data-testid': 'quiz-not-finished-title', tabindex: '-1' }, '아직 모든 문제에 답하지 않았어요.'), h('p', {}, '모든 문제에 답한 뒤에 결과를 볼 수 있어요.')));
    root.append(h('div', { class: 'wfoot' }, h('button', { class: 'primary', 'data-testid': 'quiz-continue', onclick: H.toPlay }, '이어서 풀기'), foot.firstChild));
    return root;
  }
  const r = S.result;
  if (r === null) return root;
  const q = r.quiz;
  const demo = demoBadge(q.sourceKind);
  if (demo !== null) root.append(demo);
  const refresh = h('div', { class: 'rcheck wcheck' },
    h('span', { 'data-testid': 'quiz-checked-at' }, S.checkedAt !== null ? `확인한 시각 ${hhmm(S.checkedAt)} (이 기기 시계)` : ''),
    h('button', { class: 'secondary', 'data-testid': 'quiz-refresh', disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '다시 확인'));
  if (q.status === 'stopped' || r.result === null) {
    root.append(refresh);
    root.append(h('section', { class: 'rstat', 'data-k': 'pending', 'data-testid': 'quiz-stopped', role: 'status' },
      h('h3', { 'data-testid': 'quiz-stopped-title', tabindex: '-1' }, '시험이 중단돼 결과를 보여 드릴 수 없어요.'),
      h('p', {}, '시험 문제의 근거가 바뀌어 점수와 정답은 표시하지 않아요. 이미 낸 응답은 그대로 남아 있어요. 새 시험으로 다시 풀어 주세요.')));
    const given = q.items.filter((i) => i.answered);
    if (given.length > 0) {
      root.append(h('h3', { class: 'wsub' }, '내가 낸 응답'));
      root.append(h('ul', { class: 'wlist', 'data-testid': 'quiz-given' }, ...given.map((i) => h('li', { class: 'qres', 'data-item': i.itemId },
        h('span', { class: 'whw', lang: 'en' }, i.headword), h('span', { class: 'wmn', 'data-testid': 'qgiven-label' }, optionLabel(q, i.itemId, i.selectedOptionId) ?? '')))));
    }
    root.append(h('div', { class: 'wfoot' }, h('button', { class: 'primary', 'data-testid': 'quiz-new', onclick: H.start }, '새 시험 시작'), foot.firstChild));
    return root;
  }
  root.append(refresh);
  const s = r.result;
  root.append(h('section', { class: 'rstat qscore', 'data-k': 'ok', 'data-testid': 'quiz-score' },
    h('h3', { 'data-testid': 'quiz-score-title', tabindex: '-1' }, `${s.total}문제 중 ${s.correct}문제 맞음`),
    h('p', { 'data-testid': 'quiz-score-detail' }, `맞음 ${s.correct} · 틀림 ${s.incorrect}`),
    h('p', { class: 'qsep', 'data-testid': 'quiz-no-merge' }, '단어 시험 결과예요. 과제(GR)·근거 문제 결과와 합산하지 않아요.')));
  root.append(h('ul', { class: 'wlist', 'data-testid': 'quiz-results' }, ...q.items.map((i) => h('li', { class: 'qres', 'data-item': i.itemId, 'data-correct': String(i.correct) },
    h('span', { class: 'whw', lang: 'en' }, i.headword),
    h('span', { class: 'qverdict', 'data-testid': 'qres-verdict', 'data-v': i.correct === true ? 'correct' : 'wrong' }, i.correct === true ? '맞음' : '틀림'),
    h('span', { class: 'wmn' }, h('span', { class: 'wlab' }, '내가 고른 뜻 '), h('span', { 'data-testid': 'qres-selected' }, i.selectedLabel ?? optionLabel(q, i.itemId, i.selectedOptionId) ?? '')),
    h('span', { class: 'wmn' }, h('span', { class: 'wlab' }, '정답 뜻 '), h('span', { 'data-testid': 'qres-correct' }, i.correctMeaning ?? ''))))));
  root.append(h('div', { class: 'wfoot' }, h('button', { class: 'primary', 'data-testid': 'quiz-new', onclick: H.start }, '새 시험 시작'), foot.firstChild));
  return root;
}

